<template>
  <TopNav
    :sso-token="getSsoAccessToken()"
    :show-online-offline="true"
    :hide-notification-center="!isNotificationServiceEnabled"
    :env="nodeEnv"
    :hide-right-section="$route.path === '/restricted'"
    :is-online="is_online"
    :component-code="packageCodes"
    @select="handleNotificationClick"
    @toggleStatus="onToggleStatusChange"
    @notification-center-open="onNotificationOpen"
    @unread-toggle-change="onUnreadToggleChange"
    @read="onMarkAsRead"
    @mark-all-as-read="onMarkAllAsRead"
    @filter-change="onFilterChange"
  >
    <!-- Below md only; reads the menus the unified Sidebar fetches -->
    <template #right-append>
      <MobileNavMenu />
    </template>
  </TopNav>
</template>
<script>
import { mapFields } from 'vuex-map-fields'
import { mapState } from 'vuex'
import { MobileNavMenu, TopNav } from 'qontak-unified-component/packages/vue2'
import { deleteToken } from 'firebase/messaging'
import moengage from '@moengage/web-sdk'
import { NOTIFICATION_CATEGORIES } from '../const/typeAndCategory'
import { validTokenCookie } from '~/utils/general'
import { disconnectMqttClient } from '~/utils/mqtt'
import { disconnectMqttSharedWorker } from '~/utils/mqttSharedWorker'
import { messaging } from '@/plugins/firebase'
import { generateCrmSignoutPayload } from '~/utils/auth'

export default {
  name: 'TopNavComponent',
  components: {
    MobileNavMenu,
    TopNav,
  },
  computed: {
    ...mapFields('layouts', ['userStatus.is_online']),
    ...mapState('organization', ['permissions']),
    ...mapState('layouts', ['chatbotMode', 'appConfig']),
    ...mapState('fcm', ['fcmToken']),
    ...mapState('package', [
      'isUsePackageCode',
      'packageCodes',
      'qontakPackageName',
    ]),
    isNotificationServiceEnabled() {
      return this.permissions?.use_notification_service
    },
    nodeEnv() {
      return process.env.HUB_ENVIRONMENT === 'production'
        ? 'production'
        : 'staging'
    },
  },
  watch: {
    currentRoute(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetNotificationCenterFilter()
      }
    },
  },
  mounted() {
    if (typeof this.$auth?.user?.is_online === 'boolean') {
      this.$store.commit('layouts/UPDATE_USER_STATUS', {
        is_online: this.$auth.user.is_online,
        status: this.$auth.user.is_online ? 'online' : 'offline',
      })
    } else {
      this.$store.commit('layouts/UPDATE_USER_STATUS', {
        is_online: this.userStatus.is_online,
        status: this.userStatus.is_online ? 'online' : 'offline',
      })
    }
  },
  methods: {
    getSsoAccessToken() {
      return validTokenCookie()
    },
    handleNotificationClick(notification) {
      this.$nextTick(() => {
        if (notification.origin === 'chat') {
          this.handleNotificationOnClick(notification)
        } else {
          this.redirectToCRMLink(notification)
        }

        const selectedCategory = NOTIFICATION_CATEGORIES.find(
          (t) => t.id === notification.notif_category
        )

        this.storeQontakOneMixpanelMetric(
          'Click Notification List',
          {
            'Package Name': this.qontakPackageName,
            'Company SSO ID': this.getOrganizationForMetric()['Company ID'],
            'Company Name': this.getOrganizationForMetric()['Company Name'],
            'Qontak User ID': this.$auth.user?.id,
            'Qontak Role': this.$auth.user?.role,
            'Qontak Email': this.$auth.user?.email,
            'Notification Group': selectedCategory
              ? selectedCategory.label
              : 'Unknown',
          },
          'chat',
          'qontak_one'
        )
      })
    },
    handleNotificationOnClick(notification) {
      const isAbleToUseNewInbox =
        this.permissions.revamp_inbox || this.permissions.sunset_inbox_v1
      const baseInboxUrl = isAbleToUseNewInbox ? '/inbox-v2' : '/inbox'
      let fullInboxUrl = baseInboxUrl
      if (
        notification.extra &&
        notification.extra.room &&
        (notification.extra.room.id || notification.extra.room_id)
      ) {
        fullInboxUrl = `${baseInboxUrl}?room=${
          notification.extra.room.id || notification.extra.room_id
        }`
        const isMember =
          this.$auth.user.role === 'agent' || this.$auth.user.role === 'member'
        const isResolvedNotification = notification.notif_category === '12'
        if (isMember && isResolvedNotification) {
          fullInboxUrl += '&status=resolved'
        }
        if (isAbleToUseNewInbox) window.location.replace(fullInboxUrl)
        else this.$router.push(fullInboxUrl)
      } else {
        this.$toast({
          variant: 'error',
          title: 'No room included in this notification',
          position: 'top',
        })
      }
    },
    redirectToCRMLink(notification) {
      if (notification && notification.click_action_url) {
        window.location.replace(notification.click_action_url)
      } else {
        window.location.replace(process.env.CRM_URL)
      }
    },
    signOutOnClick() {
      if (!this.chatbotMode.isEditing) {
        this.doSignOut()
        this.$store.commit('layouts/UPDATE_IS_EDITING_ON_CHATBOT', false)
        this.$store.commit('layouts/UPDATE_POP_UP_STATUS', false)
      } else {
        this.$store.commit('layouts/UPDATE_POP_UP_STATUS', true)
        this.$store.commit('layouts/UPDATE_POP_UP_TRIGGER', 'logout')
      }

      /**
       * @desc it used for reset banner height becomes 0px
       */
      this.$store.commit('layouts/SET_HEIGHT_BANNER_MAINTENANCE', 0)
      this.$store.commit('layouts/SET_HEIGHT_BANNER_USER_STATUS', 0)
      this.$store.commit('layouts/SET_HEIGHT_BANNER_CREDIT_BALANCE', 0)
    },
    async doSignOut() {
      const isUsingSSO = this.appConfig?.sso?.status
      const isAgentOrSupervisor = this.isAgent() || this.isSupervisor()
      const subscribeToken = localStorage.getItem('subscribe_token')
      const authToken = this.$auth.getToken('hub')?.split(' ')[1]
      this.removeMqttCredential()

      if (isAgentOrSupervisor && this.is_online) {
        this.$emit('onLoggedOut')
      }
      if (this.browser_type !== 'Safari' && subscribeToken) {
        await this.removeCurrentSubsToken()
        await deleteToken(messaging, subscribeToken).catch(() => {})
      }

      await this.unregistWorker()
      await moengage.destroy_session()

      // Sign out unconditionally, even with no hub token to revoke. The scheme
      // skips the network call in that case but still clears every credential
      // artifact; gating this on `authToken` skipped the teardown, so the
      // shared-domain SSO cookies survived and the next login resurrected the
      // previous account (BIF-9602).
      await this.$auth.logout({
        ...(authToken ? { token: authToken } : {}),
        ...generateCrmSignoutPayload(),
      })

      this.$store.commit('conversation/UPDATE_QUERY_ROOM', '')
      this.$store.commit('layouts/DELETE_ALL_NOTIFICATIONS')

      if (window.jimo !== null && window.jimo !== undefined) {
        // Clear localstorage data related to Jimo
        window.jimo.push(['do', 'storage:reset'])
      }

      if (isUsingSSO) {
        window.location.replace(
          `${process.env.SSO_ACCOUNT_URL}/sign_out?client_id=${process.env.SSO_UNIFIED_CLIENT_ID}`
        )
      } else {
        this.$router.push('/login')
      }
    },
    removeMqttCredential() {
      localStorage.removeItem('mqtt-credentials')
      this.handleDisconnectMqtt()
    },
    async removeCurrentSubsToken() {
      await this.$store
        .dispatch('fcm/removePreviousFcmToken', { token: this.fcmToken })
        .then((message) => {
          localStorage.removeItem('subscribe_token')
        })
        .catch((err) => {
          throw err
        })
      localStorage.removeItem('subscribe_token')
    },
    async unregistWorker() {
      /**
       * @TODO this causes sw deleted on page reload/refresh
       */
      if (window.navigator && navigator.serviceWorker) {
        await navigator.serviceWorker
          .getRegistrations()
          .then((registrations) => {
            for (const registration of registrations) {
              registration.unregister()
            }
          })
          .catch(() => {})
      }
    },
    updateUserStatus(status) {
      try {
        this.$store.dispatch('layouts/updateUserStatus', {
          is_online: status,
          status: status ? 'online' : 'offline',
        })
      } catch (error) {
        this.$toast({
          variant: 'error',
          title: 'Failure to switch status. Try again in second',
          position: 'top',
        })
      }
    },
    onToggleStatusChange(status) {
      this.updateUserStatus(status)
    },
    onNotificationOpen() {
      if (
        this.isUsePackageCode &&
        !this.packageCodes.includes('CP-QONTAKCRM-2025-0003')
      ) {
        this.$refs.notificationCenter.hideTab('Approval')
      }

      this.storeMixpanelMetric(
        'Click Notification Bell',
        {
          Role: this.$auth.user.role,
          Email: this.$auth.user.email,
          'Company ID': this.getOrganizationForMetric()['Company ID'],
          'Company Name': this.getOrganizationForMetric()['Company Name'],
          'Package Name': this.qontakPackageName,
          Route: window.location.href,
        },
        'chat',
        'qontak_one'
      )
    },
    onUnreadToggleChange(value, filter) {
      this.storeQontakOneMixpanelMetric('Show Unread Notification', {
        'Company SSO ID': this.getOrganizationForMetric()['Company ID'],
        'Company Name': this.getOrganizationForMetric()['Company Name'],
        'Package Name': this.qontakPackageName,
        'Qontak User ID': this.$auth.user?.id,
        'Qontak Role': this.$auth.user?.role,
        'Qontak Email': this.$auth.user?.email,
        'Show Unread': value,
        'Notification Group': filter
          ? filter.replaceAll('_', ' ')
          : 'No Group Recorded',
      })
    },
    onMarkAsRead() {
      this.storeQontakOneMixpanelMetric('Mark Notification As Read', {
        'Company SSO ID': this.getOrganizationForMetric()['Company ID'],
        'Company Name': this.getOrganizationForMetric()['Company Name'],
        'Package Name': this.qontakPackageName,
        'Qontak User ID': this.$auth.user?.id,
        'Qontak Role': this.$auth.user?.role,
        'Qontak Email': this.$auth.user?.email,
        Action: 'Single',
      })
    },
    onMarkAllAsRead() {
      this.storeQontakOneMixpanelMetric('Mark Notification As Read', {
        'Company SSO ID': this.getOrganizationForMetric()['Company ID'],
        'Company Name': this.getOrganizationForMetric()['Company Name'],
        'Package Name': this.qontakPackageName,
        'Qontak User ID': this.$auth.user?.id,
        'Qontak Role': this.$auth.user?.role,
        'Qontak Email': this.$auth.user?.email,
        Action: 'Bulk',
      })
    },
    onFilterChange(filter) {
      this.storeQontakOneMixpanelMetric('Filter Notification', {
        'Company SSO ID': this.getOrganizationForMetric()['Company ID'],
        'Company Name': this.getOrganizationForMetric()['Company Name'],
        'Package Name': this.qontakPackageName,
        'Qontak User ID': this.$auth.user?.id,
        'Qontak Role': this.$auth.user?.role,
        'Qontak Email': this.$auth.user?.email,
        'Notification Group': filter
          ? filter.replaceAll('_', ' ')
          : 'No Group Recorded',
      })
    },
    resetNotificationCenterFilter() {
      if (this.$refs.notificationCenter) return
      this.$refs.notificationCenter.updateTabFilter(null)
    },
    handleDisconnectMqtt() {
      if (this.permissions?.mqtt_notification) {
        if (this.permissions?.mqtt_multitab_session) {
          disconnectMqttSharedWorker()
        } else {
          disconnectMqttClient()
        }
      }
    },
  },
}
</script>
